import React from 'react';
import { Link } from 'react-router-dom';
import { useSeoMeta, useHead } from '@unhead/react';
import './LegalPage.css';

/**
 * 서비스 이용약관 페이지
 */
const TermsPage: React.FC = () => {
  // 구조화된 데이터 (Schema.org)
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '서비스 이용약관',
    description: '부냥 분양 정보 서비스의 이용약관입니다. 서비스 이용에 관한 회사와 회원 간의 권리, 의무 및 책임사항을 규정합니다.',
    url: 'https://voonang.com/terms',
    inLanguage: 'ko-KR',
    datePublished: '2024-01-01',
    dateModified: '2026-10-05',
    publisher: {
      '@type': 'Organization',
      name: '부냥',
      url: 'https://voonang.com',
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: '+82-1533-8124',
        contactType: 'customer service',
        areaServed: 'KR',
        availableLanguage: 'Korean',
      },
    },
    mainEntity: {
      '@type': 'Article',
      headline: '부냥 서비스 이용약관',
      articleSection: '이용약관',
    },
  };

  useSeoMeta({
    title: '서비스 이용약관 | 부냥',
    description: '부냥 분양 정보 서비스의 이용약관입니다. 서비스 이용에 관한 회사와 회원 간의 권리, 의무 및 책임사항을 규정합니다.',
  });
  useHead({
    link: [{ rel: 'canonical', href: 'https://voonang.com/terms' }],
    script: [{ type: 'application/ld+json', innerHTML: JSON.stringify(schemaData) }],
  });

  return (
    <div className="legal-page">
      <div className="legal-container">
        <div className="legal-header">
          <Link to="/login" className="back-link">
            뒤로가기
          </Link>
          <h1>서비스 이용약관</h1>
          <p className="effective-date">시행일자: 2026년 10월 5일 (이전 약관: 2024년 1월 1일 시행)</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>제1조 (목적)</h2>
            <p>
              이 약관은 큐비컴(서비스명 &lsquo;부냥&rsquo;, voonang.com, 이하 "회사")이 제공하는 분양 정보 서비스(이하 "서비스")의 이용과 관련하여
              회사와 회원 간의 권리, 의무 및 책임사항, 기타 필요한 사항을 규정함을 목적으로 합니다.
            </p>
          </section>

          <section>
            <h2>제2조 (정의)</h2>
            <ol>
              <li><strong>"서비스"</strong>란 회사가 제공하는 분양 현장 정보 조회, 회원 전용 할인·혜택 정보 열람, 관심 현장 저장, 상담·문의 및 알림 신청 등의 서비스를 말합니다.</li>
              <li><strong>"회원"</strong>이란 이 약관에 따라 이용계약을 체결하고, 회사가 제공하는 서비스를 이용하는 자를 말합니다.</li>
              <li><strong>"카카오 계정"</strong>이란 회원이 카카오 로그인을 통해 서비스에 접속하기 위해 사용하는 카카오 계정을 말합니다.</li>
            </ol>
          </section>

          <section>
            <h2>제3조 (약관의 효력 및 변경)</h2>
            <ol>
              <li>이 약관은 서비스 화면에 게시하거나 기타의 방법으로 회원에게 공지함으로써 효력이 발생합니다.</li>
              <li>회사는 필요한 경우 약관을 변경할 수 있으며, 변경된 약관은 제1항과 같은 방법으로 공지합니다.</li>
              <li>회원은 변경된 약관에 동의하지 않을 경우 서비스 이용을 중단하고 탈퇴할 수 있습니다.</li>
            </ol>
          </section>

          <section>
            <h2>제4조 (이용계약의 체결)</h2>
            <ol>
              <li>이용계약은 회원이 되고자 하는 자가 카카오 로그인을 통해 약관 및 개인정보 처리방침에 동의하고 가입신청을 한 후, 회사가 이를 승낙함으로써 체결됩니다.</li>
              <li>회사는 다음 각 호에 해당하는 신청에 대해서는 승낙을 하지 않거나 사후에 이용계약을 해지할 수 있습니다.
                <ul>
                  <li>다른 사람의 명의를 사용한 경우</li>
                  <li>허위의 정보를 기재한 경우</li>
                  <li>기타 이용계약 체결이 적절하지 않은 경우</li>
                </ul>
              </li>
            </ol>
          </section>

          <section>
            <h2>제5조 (서비스의 제공)</h2>
            <ol>
              <li>회사는 회원에게 다음과 같은 서비스를 제공합니다.
                <ul>
                  <li>분양 현장 정보 조회 서비스</li>
                  <li>회원 전용 할인·혜택 정보 열람 서비스 (일부 현장의 할인·혜택 상세는 로그인한 회원에게 제공)</li>
                  <li>관심 현장 저장 및 최근 본 현장 조회 서비스</li>
                  <li>분양 상담·문의, 사전상담 및 알림 신청 서비스</li>
                  <li>할인·분양 소식 안내 (마케팅 수신에 동의한 회원에 한함, 개인정보 처리방침 제7조)</li>
                  <li>기타 회사가 정하는 서비스</li>
                </ul>
              </li>
              <li>서비스는 연중무휴, 1일 24시간 제공함을 원칙으로 합니다. 단, 회사의 업무상 또는 기술상의 이유로 서비스가 일시 중단될 수 있습니다.</li>
            </ol>
          </section>

          <section>
            <h2>제5조의2 (제공 정보의 성격)</h2>
            <ol>
              <li>서비스에 게재된 분양가·할인·혜택·일정 등 분양 정보는 이용자의 이해를 돕기 위한 안내이며, 실제 공급 조건은 <strong>입주자모집공고와 분양 계약서가 우선</strong>합니다.</li>
              <li>할인·혜택은 동·호수·층, 계약 시기 및 사업주체의 정책에 따라 달라지거나 조기에 종료될 수 있으므로, 계약 전 반드시 분양 상담(1533-8124)을 통해 확인하시기 바랍니다.</li>
              <li>조감도·투시도·이미지는 소비자의 이해를 돕기 위한 것으로 실제와 다를 수 있습니다.</li>
            </ol>
          </section>

          <section>
            <h2>제6조 (회원의 의무)</h2>
            <ol>
              <li>회원은 다음 행위를 하여서는 안 됩니다.
                <ul>
                  <li>신청 또는 변경 시 허위내용의 등록</li>
                  <li>타인의 정보 도용</li>
                  <li>회사가 게시한 정보의 변경</li>
                  <li>회사가 정한 정보 이외의 정보(컴퓨터 프로그램 등) 등의 송신 또는 게시</li>
                  <li>회사와 기타 제3자의 저작권 등 지적재산권에 대한 침해</li>
                  <li>회사 및 기타 제3자의 명예를 손상시키거나 업무를 방해하는 행위</li>
                  <li>외설 또는 폭력적인 메시지, 화상, 음성, 기타 공서양속에 반하는 정보를 서비스에 공개 또는 게시하는 행위</li>
                </ul>
              </li>
            </ol>
          </section>

          <section>
            <h2>제7조 (서비스의 변경 및 중단)</h2>
            <ol>
              <li>회사는 서비스의 내용을 변경할 수 있으며, 변경된 서비스의 내용은 서비스 화면에 공지합니다.</li>
              <li>회사는 다음 각 호에 해당하는 경우 서비스의 전부 또는 일부를 제한하거나 중단할 수 있습니다.
                <ul>
                  <li>서비스용 설비의 보수 등 공사로 인한 부득이한 경우</li>
                  <li>기간통신사업자가 전기통신 서비스를 중지한 경우</li>
                  <li>기타 불가항력적 사유가 있는 경우</li>
                </ul>
              </li>
            </ol>
          </section>

          <section>
            <h2>제8조 (이용계약의 해지)</h2>
            <ol>
              <li>회원은 언제든지 마이페이지에서 회원탈퇴를 할 수 있으며, 회사는 즉시 회원탈퇴를 처리합니다.</li>
              <li>회사는 회원이 다음 각 호의 사유에 해당하는 경우 이용계약을 해지할 수 있습니다.
                <ul>
                  <li>제6조에서 정한 회원의 의무를 위반한 경우</li>
                  <li>서비스의 정상적인 운영을 방해한 경우</li>
                  <li>기타 중대한 사유로 인하여 서비스 제공을 지속하기 어려운 경우</li>
                </ul>
              </li>
            </ol>
          </section>

          <section>
            <h2>제9조 (면책조항)</h2>
            <ol>
              <li>회사는 천재지변, 전쟁 및 기타 이에 준하는 불가항력으로 인하여 서비스를 제공할 수 없는 경우에는 서비스 제공에 대한 책임이 면제됩니다.</li>
              <li>회사는 분양 정보를 정확하게 안내하도록 노력하나, 사업주체가 공고·변경한 정보가 서비스에 늦게 반영되는 등 회사의 고의 또는 중대한 과실 없이 발생한 손해에 대해서는 책임을 지지 않습니다(제5조의2).</li>
              <li>회사는 회원과 분양 사업주체 등 제3자 사이에 서비스를 매개로 발생한 분쟁에 대해 회사의 고의 또는 중대한 과실이 없는 한 손해를 배상할 책임이 없습니다.</li>
            </ol>
          </section>

          <section>
            <h2>제10조 (분쟁 해결)</h2>
            <ol>
              <li>회사와 회원은 서비스와 관련하여 발생한 분쟁을 원만하게 해결하기 위하여 상호 협력합니다.</li>
              <li>제1항의 분쟁이 해결되지 않을 경우, 양 당사자는 민사소송법상의 관할법원에 소를 제기할 수 있습니다.</li>
            </ol>
          </section>

          <section>
            <h2>부칙</h2>
            <p>이 약관은 2026년 10월 5일부터 시행합니다. (이전 약관: 2024년 1월 1일 시행)</p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
